import moment from 'moment';
import { path, pipe } from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { connect, RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ThemeProvider, Theme, StyledEngineProvider, Switch } from "@mui/material";
import { ReactComponent as BookmarkSvg } from '../../../../assets/svg/bookmarks.svg';
import { ReactComponent as CalculateSvg } from '../../../../assets/svg/calculate.svg';
import { ReactComponent as CalendarToday } from '../../../../assets/svg/calendar_today.svg';
import { ReactComponent as DevicesSvg } from '../../../../assets/svg/devices.svg';
import { ReactComponent as LayerSvg } from '../../../../assets/svg/layers.svg';
import { ReactComponent as ListAltSvg } from "../../../../assets/svg/list_alt_new.svg";
import { ReactComponent as PeopleSvg } from '../../../../assets/svg/people.svg';
import { ReactComponent as SpeedSvg } from "../../../../assets/svg/track.svg";
import { ReactComponent as WarningAmberSvg } from '../../../../assets/svg/warning_amber.svg';
import { ACL, UserCan } from '../../../../shared/constants';
import { getReportFilterVisibility } from "../../../../shared/db/extra-info-db";
import { isMobile, lEmpty } from '../../../../shared/helpers';
import { iFullStoreState, ReportFilterSectionName } from '../../../../shared/interfaces';
import { localStorage } from '../../../../shared/storage';
import { GMapModalAC } from '../../../../stores/reducers/gMapModal/AC';
import { Actions as ReportActions } from '../../../../stores/reducers/report-reducers';
import { muiSwitchTheme } from '../../../../utils/muiThemes';
import { Button, ButtonSize, ButtonStyles } from '../../../Button';
import { Collapsible } from '../../../general/Collapsible';
import Loader from "../../Billing/Other/Loader";
import { DaysChooser, RangeDateChooser } from '../DateChoosers';
import { ReportPageFilterSection } from '../ReportPageFilterSection';
import {
  alertsSelector,
  deviceSelector,
  labelsSelector,
  personSelector,
  tagsSelector,
  typeSelector
} from '../ReportSelectors';
import { FilterType, IPropsFromStore } from './ReportPageFilters.interfaces';
import { Rnd } from 'react-rnd';





export const checkingSelectedFiltersCount = ({currentFilters}) => {
  const filtersItemCount = Object.entries(currentFilters).reduce((acc, [key, value]) => ({
    ...acc,
    [key]: Object.keys(value).length
  }), {});

  return filtersItemCount
}

const mapStateToProps = (state: iFullStoreState): IPropsFromStore => {
  const reportTypeRedux = typeof state.report.details.reportTypeFromRedux == 'undefined' ? 'travel' : state.report.details.reportTypeFromRedux
  return ({
    visDevices: deviceSelector(state),
    visTypes: typeSelector(state),
    visPeople: personSelector(state),
    visTags: tagsSelector(state),
    visAlerts: alertsSelector(state),
    visLabels: labelsSelector(state),
    dates: state.report.details.dates,
    reportKey: state.report.details.searchKey,
    dateError: state.report.dateError,
    labels: state.general.tripLabels,
    devicesDetails: state.devicesData.devicesDetails,
    people: state.general.people,
    tags: state.general.tags,
    filterStrs: state.report.filterFilters,
    currentFilters: state.report.details.filters,
    userCan: state.auth.user.acl.can,
    reportTypeFromRedux: reportTypeRedux,
  })
};

const ReportPageFilter = (props) => {
  const [multiDate, setMultiDate] = useState<boolean>(props.dates.length !== 1);
  const [deviceFilter, setDeviceFilter] = useState<string>('');
  const [startKey, setStartKey] = useState<string>(props.reportKey);
  const [startOfDateChange, setStartOfDateChange] = useState<moment.Moment>();
  const [endOfDateChange, setEndOfDateChange] = useState<moment.Moment>();
  const state = useSelector((state: RootStateOrAny) => state);
  const currentPostedSpeed = state.report.details.filters[FilterType.POSTED_SPEED];

  const countStartEndDateSafe = () => {
    const {dates} = props;

    if (dates.length === 0) return null;

    let startDate, endDate;

    if (multiDate) {
      startDate = moment.min(dates.map(d => d.startDate));
      endDate = moment.max(dates.map(d => d.ended));
    } else {
      startDate = dates[0].startDate;
      endDate = dates[0].endDate;
    }

    return {startDate, endDate};
  }

  const handleClearAll = () => {
    props.dispatch(ReportActions.RESET_ALL_FILTERS());
    props.dispatch(ReportActions.RESET_DATES());

    setStartOfDateChange(undefined);
    setEndOfDateChange(undefined);
    setMultiDate(true);
  }

  const {
    visDevices,
    filterStrs,
    dispatch,
    visPeople,
    visTypes,
    dateError,
    visTags,
    visAlerts,
    visLabels,
    reportKey,
    currentFilters,
    devicesDetails,
    people,
    tags,
    labels,
    dates,
    userCan,
    reportTypeFromRedux
  } = props;
  const seePeopleFilter = ACL.check(UserCan.FILTER_REPORTS_BY_PEOPLE, userCan) && !['video', 'activity', 'mileage', 'inventory', 'expiration', 'service'].includes(reportTypeFromRedux);
  const seeDevicesFilter = reportTypeFromRedux !== 'permissions' && reportTypeFromRedux !== 'expiration' && reportTypeFromRedux !== 'service';
  const seeTagsFilter = reportTypeFromRedux !== 'permissions' && reportTypeFromRedux !== 'expiration' && reportTypeFromRedux !== 'service';
  const seeAlertsFilter = !['video', 'permissions', 'activity', 'mileage', 'inventory', 'expiration', 'service'].includes(reportTypeFromRedux);
  const seeValuesFilter = reportTypeFromRedux === 'static' && reportTypeFromRedux !== 'expiration' && reportTypeFromRedux !== 'service';
  const seeLabelsFilter = !['video', 'permissions', 'activity', 'mileage', 'inventory', 'expiration', 'service'].includes(reportTypeFromRedux);
  const seeDateFilter = !['permissions', 'activity', 'inventory', 'expiration'].includes(reportTypeFromRedux);
  const seePostedSpeedFilter = reportTypeFromRedux === 'static' && reportTypeFromRedux !== 'expiration' && reportTypeFromRedux !== 'service';

  const isAdmin = ACL.check(UserCan.DO_ANYTHING, userCan, true);
  const dateDisplay = countStartEndDateSafe();
  const countOfSelectedFilters = checkingSelectedFiltersCount({currentFilters});
  const loginToken = localStorage.get('login-init-be-token');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filtersVisibility, setFiltersVisibility] = useState<Record<ReportFilterSectionName, boolean>>(null);

  const containerRef = useRef(null);
  const [defaultPosition, setDefaultPosition] = useState(null);

  const MODAL_WIDTH = 750;
  const MODAL_HEIGHT = 585;
  const resizeHandleSize = 10;

  const DATE_RANGE_PRESETS = [
    { label: 'Today', getRange: () => ({ startDate: moment().startOf('day'), endDate: moment().endOf('day') }) },
    { label: 'Yesterday', getRange: () => ({ startDate: moment().subtract(1, 'day').startOf('day'), endDate: moment().subtract(1, 'day').endOf('day') }) },
    { label: 'Last Week', getRange: () => ({ startDate: moment().subtract(1, 'week').startOf('isoWeek'), endDate: moment().subtract(1, 'week').endOf('isoWeek') }) },
    { label: 'Last Month', getRange: () => ({ startDate: moment().subtract(1, 'month').startOf('month'), endDate: moment().subtract(1, 'month').endOf('month') }) },
    { label: 'Last Quarter', getRange: () => ({ startDate: moment().subtract(1, 'quarter').startOf('quarter'), endDate: moment().subtract(1, 'quarter').endOf('quarter') }) },
    { label: 'This Week', getRange: () => ({ startDate: moment().startOf('isoWeek'), endDate: moment().endOf('isoWeek') }) },
    { label: 'This Month', getRange: () => ({ startDate: moment().startOf('month'), endDate: moment().endOf('month') }) },
    { label: 'This Quarter', getRange: () => ({ startDate: moment().startOf('quarter'), endDate: moment().endOf('quarter') }) },
  ];

  const enableResizing = {
    top: true,
    right: true,
    bottom: true,
    left: true,
    topRight: true,
    bottomRight: true,
    bottomLeft: true,
    topLeft: true,
  };

  const resizeHandleStyles = {
    top: { top: 0, left: 0, right: 0, height: resizeHandleSize, cursor: 'ns-resize', zIndex: 2001 },
    right: { top: 0, right: 0, bottom: 0, width: resizeHandleSize, cursor: 'ew-resize', zIndex: 2001 },
    bottom: { left: 0, right: 0, bottom: 0, height: resizeHandleSize, cursor: 'ns-resize', zIndex: 2001 },
    left: { top: 0, left: 0, bottom: 0, width: resizeHandleSize, cursor: 'ew-resize', zIndex: 2001 },
    topRight: { top: 0, right: 0, width: resizeHandleSize, height: resizeHandleSize, cursor: 'nesw-resize', zIndex: 2002 },
    bottomRight: { right: 0, bottom: 0, width: resizeHandleSize, height: resizeHandleSize, cursor: 'nwse-resize', zIndex: 2002 },
    bottomLeft: { left: 0, bottom: 0, width: resizeHandleSize, height: resizeHandleSize, cursor: 'nesw-resize', zIndex: 2002 },
    topLeft: { top: 0, left: 0, width: resizeHandleSize, height: resizeHandleSize, cursor: 'nwse-resize', zIndex: 2002 },
  };

  useEffect(() => {
    if (containerRef.current) {
      const parentWidth = containerRef.current.offsetWidth;
      const parentHeight = containerRef.current.offsetHeight;

      setDefaultPosition({
        x: (parentWidth / 2) - (MODAL_WIDTH / 2),
        y: (parentHeight / 2) - (MODAL_HEIGHT / 2),
        width: MODAL_WIDTH,
        height: MODAL_HEIGHT
      });
    }
  }, []);

  useEffect(() => {
    const fetchReportFilterVisibility = async () => {
      try {
        const value = await getReportFilterVisibility();
        if (value) {
          setFiltersVisibility(value);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    }

    fetchReportFilterVisibility();
  }, []);

  const onPostedSpeedUpdate = (e) => {
    const isChecked = e.target.checked;

    dispatch(ReportActions.SET_POSTED_SPEED(FilterType.POSTED_SPEED, isChecked));
  }

  const handleDateRangePreset = (preset) => {
    const { startDate, endDate } = preset.getRange();
    setStartOfDateChange(startDate);
    setEndOfDateChange(endDate);
    setMultiDate(false);
  }

  const rangeStart = startOfDateChange || path([0, 'startDate'])(dates);
  const rangeEnd = endOfDateChange || path([0, 'endDate'])(dates);
  const rangeChooserKey = `${rangeStart ? moment(rangeStart).valueOf() : 'none'}-${rangeEnd ? moment(rangeEnd).valueOf() : 'none'}`;

  const renderPageFilterSection = (filtersVisibilityObj: Record<ReportFilterSectionName, boolean>) => {
    if (!filtersVisibilityObj) return null;
    const { type, devices, people, tags, alerts, values, labels, posted_speed, date } = filtersVisibilityObj;
    const renderedSections = [];
    if (type) {
      renderedSections.push(
        <ReportPageFilterSection
          Icon={ListAltSvg}
          title="Report"
          visible={isAdmin ? visTypes : visTypes.filter((type) => type.key !== 'audit-log')}
          filterType={FilterType.TYPES}
          countOfSelectedFilters={countOfSelectedFilters}
          RedirectToTabs={true}
          searchInputPlaceholder='Search Report Type'
        />
      )
    }
    if (devices && seeDevicesFilter) {
      renderedSections.push(<ReportPageFilterSection
                disabled={!lEmpty(currentFilters[FilterType.TAGS])}
                Icon={DevicesSvg}
                title="Devices"
                visible={visDevices}
                filterType={FilterType.DEVICES}
                countOfSelectedFilters={countOfSelectedFilters}
                searchInputPlaceholder='Filter Devices'
            />
      )
    }
    if (people && seePeopleFilter) {
      renderedSections.push(
        <ReportPageFilterSection
          disabled={!lEmpty(currentFilters[FilterType.TAGS])}
          Icon={PeopleSvg}
          visible={visPeople}
          title="People"
          filterType={FilterType.PEOPLE}
          countOfSelectedFilters={countOfSelectedFilters}
          searchInputPlaceholder='Filter People'
        />
      )
    }
    if (tags !== false && seeTagsFilter) {
      renderedSections.push(
        <ReportPageFilterSection
          Icon={LayerSvg}
          disabled={!lEmpty(currentFilters[FilterType.PEOPLE]) || !lEmpty(currentFilters[FilterType.DEVICES])}
          title="Tags"
          visible={visTags}
          filterType={FilterType.TAGS}
          countOfSelectedFilters={countOfSelectedFilters}
          searchInputPlaceholder='Filter Tags'
        />
      )
    }
    if (alerts && seeAlertsFilter) {
      renderedSections.push(
        <ReportPageFilterSection
          Icon={WarningAmberSvg}
          title="Alerts"
          RedirectToStatic
          visible={visAlerts}
          filterType={FilterType.ALERTS}
          countOfSelectedFilters={countOfSelectedFilters}
          searchInputPlaceholder='Filter Alert'
        />
      )
    }
    if (values && seeValuesFilter) {
      renderedSections.push(
        <ReportPageFilterSection
          Icon={CalculateSvg}
          title="Values"
          hasSelectionsGrid
          filterType={FilterType.VALUES}
          countOfSelectedFilters={countOfSelectedFilters}
        />
      )
    }
    if (labels && seeLabelsFilter) {
      renderedSections.push(
        <ReportPageFilterSection
          Icon={BookmarkSvg}
          title="Labels"
          visible={visLabels}
          filterType={FilterType.LABELS}
          countOfSelectedFilters={countOfSelectedFilters}
          searchInputPlaceholder='Filter Labels'
        />
      )
    }
    if (posted_speed && seePostedSpeedFilter) {
      renderedSections.push(
        <div className='report-filter-section'>
            <Collapsible
              header={
                <div className="report-filter-labels-header">
                  <i className='report-filter-icon'><SpeedSvg /></i>
                  <span>Posted Speed</span>
                </div>
              }
              isCollapsed={true}
              className="report-filter"
              headerClassName="report-filter-header"
              bodyClassName="report-filter-body"
            >
              <label className='switcher'>
                <StyledEngineProvider injectFirst>
                  <ThemeProvider theme={muiSwitchTheme}>
                    Is posted speed filter applied? <Switch checked={currentPostedSpeed} onChange={onPostedSpeedUpdate} />
                  </ThemeProvider>
                </StyledEngineProvider>
              </label>
            </Collapsible>
          </div>
      )
    }
    if (date !== false && seeDateFilter) {
      renderedSections.push(
        <div className="date-choser-filter-section report-filter-section">
          <Collapsible
            isCollapsed={true}
            className="report-filter"
            headerClassName="report-filter-header"
            bodyClassName="report-filter-body"
            scrollIntoView
            header={
              <div className="report-filter-labels-header">
                <i className="report-filter-icon"><CalendarToday/></i>
                <span>
                Date/Range {
                      dateDisplay && (
                        <span className="date weight-normal">
                      ({dateDisplay.startDate.format("MM/DD/YYYY")} - {dateDisplay.endDate.format("MM/DD/YYYY")})
                    </span>
                      )
                    }
              </span>
                  </div>
                }
              >
                <div>
                  <div className={`date-filter-tabs`}>
                <span
                  onClick={() => {
                    dispatch(ReportActions.RESET_DATES());
                    setMultiDate(true);
                  }}
                  className={`date-tab ${multiDate ? "active" : ""}`}>
                  Date
                </span>
                    <span
                      onClick={() => {
                        dispatch(ReportActions.RESET_DATES());
                        setMultiDate(false);
                      }}
                      className={`date-tab ${!multiDate ? "active" : ""}`}>
                  Range
                </span>
                  </div>
                  {!dateError ? <div style={{height: 3}}/> : <div style={{
                    marginTop: 8,
                    color: 'darkred',
                    textAlign: 'center',
                    marginBottom: -25,
                    paddingBottom: 8
                  }}>{dateError}</div>}

                  <div className={`date-range-layout ${multiDate ? 'date-mode' : 'range-mode'}`} style={{marginTop: 15}}>
                    <div className="date-range-calendar">
                      {multiDate
                        ? <DaysChooser
                          dates={dates}
                          remove={pipe(ReportActions.REMOVE_DATE_GROUP_IN_DATES, dispatch)}
                          dispatch={dispatch}
                          startDateChange={(el) => {
                            setStartOfDateChange(el);
                          }}
                          endDateChange={(el) => {
                            setEndOfDateChange(el);
                          }}
                        />
                        : <RangeDateChooser
                          key={rangeChooserKey}
                          style={dateError ? {borderColor: 'darkred', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', flexWrap: 'nowrap', rowGap: 8} : {flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', flexWrap: 'nowrap', rowGap: 8}}
                          initialStart={rangeStart}
                          initialEnd={rangeEnd}
                          calendarHideable={false}
                          calendarTop
                          startDateChange={(el) => {
                            setStartOfDateChange(el);
                          }}
                          endDateChange={(el) => {
                            setEndOfDateChange(el);
                          }}
                        />
                      }
                    </div>
                    <div className="date-range-presets">
                      {DATE_RANGE_PRESETS.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          className="date-range-preset-btn"
                          onClick={() => handleDateRangePreset(preset)}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Collapsible>
            </div>
      )
    }
    return renderedSections;
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
    {defaultPosition && (
      <Rnd
        className="report-filters-rnd"
        default={defaultPosition}
        minWidth={MODAL_WIDTH}
        minHeight={MODAL_HEIGHT}
        bounds="parent"
        enableResizing={enableResizing}
        resizeHandleStyles={resizeHandleStyles}
        resizeHandleClasses={{
          bottomRight: 'report-filters-rnd__handle-bottom-right',
        }}
        dragHandleClassName="report-filter-wraper"
        style={{ zIndex: 2000 }}
        cancel=".report-filter-section "
      >
        <div className="report-filter-wraper">
          <div className="report-filters-header-fixed">
            <h3>Report Filters</h3>
          </div>
          <PerfectScrollbar>
            <div className="report-filters" style={{display: 'flex', flexWrap: 'nowrap', flexDirection: 'column'}}>
              {isLoading ?
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '48%',
                  transform: 'translate(-50%, -50%)'
                }}>
                  <Loader/>
                </div>: (
                <>
                  {renderPageFilterSection(filtersVisibility)}
                </>
              )}
            </div>
          </PerfectScrollbar>
          <FiltersActions
            handleClearAll={handleClearAll}
            multiDate={multiDate}
            reportTypeFromRedux={reportTypeFromRedux}
            startOfDateChange={startOfDateChange}
            endOfDateChange={endOfDateChange}
          />
        </div>
      </Rnd>
    )}
    </div>
  )
}

export default connect(mapStateToProps)(ReportPageFilter);

const FiltersActions = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    const unique = {
      uniqueTripPath: [],
      isSelectedAreaMode: false,
    }

    dispatch(ReportActions.SET_TRIPS_FROM_SELECTED_AREA(unique));
  }, [props.startOfDateChange, props.endOfDateChange])

  const clearRecords = () => {
    dispatch({type: 'REPORT_SET_DISPLAY_RECORDS', displayRecords: {}});
  }

  const handleClose = () => dispatch(GMapModalAC.hideModal());

  const handleApply = () => {
    history.push(`/reports/new/${props.reportTypeFromRedux}`);

    if (props.startOfDateChange || props.endOfDateChange) {
      if (!props.startOfDateChange.isValid() && !props.endOfDateChange.isValid()) {
        dispatch(ReportActions.RESET_DATES());
      }
      else {
        dispatch(ReportActions.SET_START_DATE(props.startOfDateChange));
        dispatch(ReportActions.SET_END_DATE(props.endOfDateChange));
      }
    }

    dispatch(GMapModalAC.hideModal());
  }

  return (
    <div className="report-filters-actions">
      {!(isMobile || window['cordova']) &&
          <div>
              <Button
                  styleType={ButtonStyles.GRAY_INACTIVE}
                  size={ButtonSize.MD}
                  onClick={props.handleClearAll}>
                  Clear all
              </Button>
          </div>
      }
      <div className={(isMobile || window['cordova']) ? 'w100' : ''}>
        <Button
          styleType={ButtonStyles.WHITE_GRAY}
          size={(isMobile || window['cordova']) ? ButtonSize.LG : ButtonSize.MD}
          onClick={handleClose}>
          Cancel
        </Button>
        <Button
          style={{marginLeft: "5px"}}
          styleType={ButtonStyles.ORANGE_WHITE}
          size={(isMobile || window['cordova']) ? ButtonSize.LG : ButtonSize.MD}
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}
